import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ConsultationBooking = ({ selectedPathway, onBookingComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    
    // Consultation Details
    consultationType: selectedPathway?.id || '',
    preferredDate: '',
    preferredTime: '',
    timezone: '',
    meetingFormat: '',
    
    // Pre-qualification
    companySize: '',
    industry: '',
    currentAIUsage: '',
    budget: '',
    timeline: '',
    primaryGoals: '',
    specificChallenges: '',
    
    // Additional Information
    additionalNotes: '',
    confidentialityLevel: '',
    decisionMakers: ''
  });

  const [errors, setErrors] = useState({});

  const consultationTypes = [
    { value: 'strategic', label: 'Strategic Consultation - C-Suite & Business Leaders' },
    { value: 'technical', label: 'Technical Assessment - IT Leaders & Technical Teams' },
    { value: 'partnership', label: 'Partnership Inquiry - Strategic Alliances' }
  ];

  const companySizes = [
    { value: 'startup', label: 'Startup (1-50 employees)' },
    { value: 'small', label: 'Small Business (51-200 employees)' },
    { value: 'medium', label: 'Medium Enterprise (201-1000 employees)' },
    { value: 'large', label: 'Large Enterprise (1001-5000 employees)' },
    { value: 'enterprise', label: 'Fortune 500 (5000+ employees)' }
  ];

  const industries = [
    { value: 'technology', label: 'Technology & Software' },
    { value: 'financial', label: 'Financial Services' },
    { value: 'healthcare', label: 'Healthcare & Life Sciences' },
    { value: 'manufacturing', label: 'Manufacturing & Industrial' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'energy', label: 'Energy & Utilities' },
    { value: 'government', label: 'Government & Public Sector' },
    { value: 'other', label: 'Other' }
  ];

  const timezones = [
    { value: 'EST', label: 'Eastern Time (EST)' },
    { value: 'CST', label: 'Central Time (CST)' },
    { value: 'MST', label: 'Mountain Time (MST)' },
    { value: 'PST', label: 'Pacific Time (PST)' },
    { value: 'UTC', label: 'UTC/GMT' }
  ];

  const meetingFormats = [
    { value: 'video', label: 'Video Conference (Preferred)' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'in-person', label: 'In-Person Meeting' },
    { value: 'hybrid', label: 'Hybrid (Video + In-Person)' }
  ];

  const budgetRanges = [
    { value: 'under-50k', label: 'Under $50,000' },
    { value: '50k-100k', label: '$50,000 - $100,000' },
    { value: '100k-250k', label: '$100,000 - $250,000' },
    { value: '250k-500k', label: '$250,000 - $500,000' },
    { value: '500k-1m', label: '$500,000 - $1,000,000' },
    { value: 'over-1m', label: 'Over $1,000,000' },
    { value: 'tbd', label: 'To Be Determined' }
  ];

  const timelineOptions = [
    { value: 'immediate', label: 'Immediate (Within 30 days)' },
    { value: 'short', label: 'Short-term (1-3 months)' },
    { value: 'medium', label: 'Medium-term (3-6 months)' },
    { value: 'long', label: 'Long-term (6+ months)' },
    { value: 'planning', label: 'Planning Phase' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
      if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
      if (!formData?.email?.trim()) newErrors.email = 'Email is required';
      if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
      if (!formData?.company?.trim()) newErrors.company = 'Company name is required';
      if (!formData?.title?.trim()) newErrors.title = 'Job title is required';
    }

    if (step === 2) {
      if (!formData?.consultationType) newErrors.consultationType = 'Consultation type is required';
      if (!formData?.preferredDate) newErrors.preferredDate = 'Preferred date is required';
      if (!formData?.preferredTime) newErrors.preferredTime = 'Preferred time is required';
      if (!formData?.timezone) newErrors.timezone = 'Timezone is required';
      if (!formData?.meetingFormat) newErrors.meetingFormat = 'Meeting format is required';
    }

    if (step === 3) {
      if (!formData?.companySize) newErrors.companySize = 'Company size is required';
      if (!formData?.industry) newErrors.industry = 'Industry is required';
      if (!formData?.primaryGoals?.trim()) newErrors.primaryGoals = 'Primary goals are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = () => {
    if (validateStep(currentStep)) {
      // Simulate booking submission
      console.log('Booking submitted:', formData);
      onBookingComplete(formData);
    }
  };

  const steps = [
    { number: 1, title: 'Personal Information', icon: 'User' },
    { number: 2, title: 'Consultation Details', icon: 'Calendar' },
    { number: 3, title: 'Pre-qualification', icon: 'CheckSquare' },
    { number: 4, title: 'Additional Information', icon: 'FileText' }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Schedule Your Consultation
          </h2>
          <p className="text-xl text-slate-600">
            Complete the form below to book your personalized consultation session.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-between">
            {steps?.map((step, index) => (
              <React.Fragment key={step?.number}>
                <div className="flex flex-col items-center">
                  <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 enterprise-transition ${
                    currentStep >= step?.number
                      ? 'bg-primary border-primary text-white' :'bg-white border-border text-text-secondary'
                  }`}>
                    {currentStep > step?.number ? (
                      <Icon name="Check" size={20} />
                    ) : (
                      <Icon name={step?.icon} size={20} />
                    )}
                  </div>
                  <span className={`mt-2 text-sm font-medium ${
                    currentStep >= step?.number ? 'text-primary' : 'text-text-secondary'
                  }`}>
                    {step?.title}
                  </span>
                </div>
                {index < steps?.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    currentStep > step?.number ? 'bg-primary' : 'bg-border'
                  }`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-surface rounded-2xl p-8">
          {currentStep === 1 && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-text-primary mb-6">Personal Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="First Name"
                  type="text"
                  placeholder="Enter your first name"
                  value={formData?.firstName}
                  onChange={(e) => handleInputChange('firstName', e?.target?.value)}
                  error={errors?.firstName}
                  required
                />
                
                <Input
                  label="Last Name"
                  type="text"
                  placeholder="Enter your last name"
                  value={formData?.lastName}
                  onChange={(e) => handleInputChange('lastName', e?.target?.value)}
                  error={errors?.lastName}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="Enter your email"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  error={errors?.email}
                  required
                />
                
                <Input
                  label="Phone Number"
                  type="tel"
                  placeholder="Enter your phone number"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  error={errors?.phone}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Company Name"
                  type="text"
                  placeholder="Enter your company name"
                  value={formData?.company}
                  onChange={(e) => handleInputChange('company', e?.target?.value)}
                  error={errors?.company}
                  required
                />
                
                <Input
                  label="Job Title"
                  type="text"
                  placeholder="Enter your job title"
                  value={formData?.title}
                  onChange={(e) => handleInputChange('title', e?.target?.value)}
                  error={errors?.title}
                  required
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-text-primary mb-6">Consultation Details</h3>
              
              <Select
                label="Consultation Type"
                options={consultationTypes}
                value={formData?.consultationType}
                onChange={(value) => handleInputChange('consultationType', value)}
                error={errors?.consultationType}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Preferred Date"
                  type="date"
                  value={formData?.preferredDate}
                  onChange={(e) => handleInputChange('preferredDate', e?.target?.value)}
                  error={errors?.preferredDate}
                  required
                  min={new Date()?.toISOString()?.split('T')?.[0]}
                />
                
                <Input
                  label="Preferred Time"
                  type="time"
                  value={formData?.preferredTime}
                  onChange={(e) => handleInputChange('preferredTime', e?.target?.value)}
                  error={errors?.preferredTime}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Select
                  label="Timezone"
                  options={timezones}
                  value={formData?.timezone}
                  onChange={(value) => handleInputChange('timezone', value)}
                  error={errors?.timezone}
                  required
                />
                
                <Select
                  label="Meeting Format"
                  options={meetingFormats}
                  value={formData?.meetingFormat}
                  onChange={(value) => handleInputChange('meetingFormat', value)}
                  error={errors?.meetingFormat}
                  required
                />
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-text-primary mb-6">Pre-qualification Questions</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Select
                  label="Company Size"
                  options={companySizes}
                  value={formData?.companySize}
                  onChange={(value) => handleInputChange('companySize', value)}
                  error={errors?.companySize}
                  required
                />
                
                <Select
                  label="Industry"
                  options={industries}
                  value={formData?.industry}
                  onChange={(value) => handleInputChange('industry', value)}
                  error={errors?.industry}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Select
                  label="Project Budget Range"
                  options={budgetRanges}
                  value={formData?.budget}
                  onChange={(value) => handleInputChange('budget', value)}
                />
                
                <Select
                  label="Implementation Timeline"
                  options={timelineOptions}
                  value={formData?.timeline}
                  onChange={(value) => handleInputChange('timeline', value)}
                />
              </div>

              <Input
                label="Primary AI Goals"
                type="text"
                placeholder="What are your main objectives for AI implementation?"
                value={formData?.primaryGoals}
                onChange={(e) => handleInputChange('primaryGoals', e?.target?.value)}
                error={errors?.primaryGoals}
                required
              />

              <Input
                label="Current AI Usage"
                type="text"
                placeholder="Describe any existing AI tools or initiatives"
                value={formData?.currentAIUsage}
                onChange={(e) => handleInputChange('currentAIUsage', e?.target?.value)}
              />

              <Input
                label="Specific Challenges"
                type="text"
                placeholder="What challenges are you hoping to address?"
                value={formData?.specificChallenges}
                onChange={(e) => handleInputChange('specificChallenges', e?.target?.value)}
              />
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-text-primary mb-6">Additional Information</h3>
              
              <Input
                label="Decision Makers"
                type="text"
                placeholder="Who else will be involved in the decision-making process?"
                value={formData?.decisionMakers}
                onChange={(e) => handleInputChange('decisionMakers', e?.target?.value)}
                description="This helps us prepare appropriate materials and expertise"
              />

              <Select
                label="Confidentiality Level"
                options={[
                  { value: 'standard', label: 'Standard Business Discussion' },
                  { value: 'confidential', label: 'Confidential - NDA Required' },
                  { value: 'highly-confidential', label: 'Highly Confidential - Enhanced Security' }
                ]}
                value={formData?.confidentialityLevel}
                onChange={(value) => handleInputChange('confidentialityLevel', value)}
                description="Select the appropriate confidentiality level for your discussion"
              />

              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-primary">
                  Additional Notes
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  rows={4}
                  placeholder="Any additional information or special requirements..."
                  value={formData?.additionalNotes}
                  onChange={(e) => handleInputChange('additionalNotes', e?.target?.value)}
                />
              </div>

              <div className="bg-white rounded-lg p-6 border border-border">
                <h4 className="font-semibold text-text-primary mb-4">What to Expect Next:</h4>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <Icon name="Clock" size={16} className="mr-3 mt-0.5 text-primary" />
                    <span className="text-sm text-text-secondary">
                      Confirmation email within 2 hours with meeting details
                    </span>
                  </div>
                  <div className="flex items-start">
                    <Icon name="User" size={16} className="mr-3 mt-0.5 text-primary" />
                    <span className="text-sm text-text-secondary">
                      Expert matching based on your specific needs and industry
                    </span>
                  </div>
                  <div className="flex items-start">
                    <Icon name="FileText" size={16} className="mr-3 mt-0.5 text-primary" />
                    <span className="text-sm text-text-secondary">
                      Pre-consultation materials and agenda sent 24 hours before
                    </span>
                  </div>
                  <div className="flex items-start">
                    <Icon name="Shield" size={16} className="mr-3 mt-0.5 text-primary" />
                    <span className="text-sm text-text-secondary">
                      All discussions covered by our standard confidentiality agreement
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-12 pt-8 border-t border-border">
            <div>
              {currentStep > 1 && (
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  Previous
                </Button>
              )}
            </div>
            
            <div>
              {currentStep < 4 ? (
                <Button
                  variant="default"
                  onClick={handleNext}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  Next Step
                </Button>
              ) : (
                <Button
                  variant="default"
                  onClick={handleSubmit}
                  iconName="Send"
                  iconPosition="right"
                  className="bg-success hover:bg-success/90"
                >
                  Schedule Consultation
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsultationBooking;